'use client';
import React, { createContext, useContext } from 'react';

import { Tabs } from 'antd';

import classNames from 'classnames';
import { CloseFile } from '@whelk/assets/src/web/icons';
import { FilePreview, FilePreviewProps } from './components/FilePreview';
import { SearchList, SearchListProps } from './components/SearchList';

import './index.scss';
import { getFileExtension, outFocus } from '@whelk/utils';
import { getFileSvg } from '@whelk/components/src/Base/FileCard/FileIcon';
import { isNative, isOverseas } from '@whelk/const/src/env';

type FileTabInfo = FilePreviewProps & {
  type: 'file';
  title: string;
  key: string;
};

type SearchTabInfo = SearchListProps & {
  type: 'search';
  title: string;
  key: string;
};

type TabInfo = FileTabInfo | SearchTabInfo;

type RightTabsModelContext = {
  open: boolean;
  activeKey: string | undefined;
  tabList: TabInfo[];
  setOpen: (open: boolean) => void;
  setTablist: (list: TabInfo[]) => void;
  addTab: (tab: Omit<FileTabInfo, 'key'> | Omit<SearchTabInfo, 'key'>) => void;
  delTab: (tab: TabInfo) => void;
  toggleSidebar: () => void;
  cleanTabList: () => void;
  setActiveKey: (key: string | undefined) => void;
  previewTab: (tab: Omit<FileTabInfo, 'key'> | Omit<SearchTabInfo, 'key'>) => void;
};

const RightTabsModelContext = createContext<RightTabsModelContext | null>(null);

const RightTabsModelProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = React.useState(true);
  const [tabList, setTablist] = React.useState<TabInfo[]>([]);
  const [activeKey, setActiveKey] = React.useState<string | undefined>(undefined);

  const cleanTabList = () => {
    setTablist([]);
  };

  const toggleSidebar = () => {
    setOpen((open) => !open);
  };

  const addTab = (tab: Omit<FileTabInfo, 'key'> | Omit<SearchTabInfo, 'key'>) => {
    const tabWithKey: TabInfo = {
      ...tab,
      key: Date.now().toString(),
    };
    setTablist((list) => [...list, tabWithKey]);
    setActiveKey(tabWithKey.key);
    if (isNative && open) {
      outFocus();
    }
  };

  const previewTab = (tab: Omit<FileTabInfo, 'key'> | Omit<SearchTabInfo, 'key'>) => {
    setOpen(true);
    const tabWithKey: TabInfo = {
      ...tab,
      key: Date.now().toString(),
    };
    setTablist([tabWithKey]);
    setActiveKey(tabWithKey.key);
    if (isNative) {
      outFocus();
    }
  };

  const delTab = (tab: TabInfo) => {
    if (tabList.length === 1) {
      setOpen(false);
    }
    setTablist((list) => list.filter((item) => item !== tab));
  };

  const contextValue = React.useMemo<RightTabsModelContext>(
    () => ({
      activeKey,
      open,
      setOpen,
      toggleSidebar,
      setActiveKey,
      tabList,
      previewTab,
      setTablist,
      cleanTabList,
      addTab,
      delTab,
    }),
    [
      tabList,
      activeKey,
      open,
      setOpen,
      toggleSidebar,
      setActiveKey,
      setTablist,
      previewTab,
      cleanTabList,
      addTab,
      delTab,
    ],
  );

  return (
    <RightTabsModelContext.Provider value={contextValue}>{children}</RightTabsModelContext.Provider>
  );
};

function useRightTabsModel() {
  const context = useContext(RightTabsModelContext);
  if (!context) {
    // throw new Error('useRightTabsModel must be used within a RightTabsModelProvider');
    console.error('useRightTabsModel must be used within a RightTabsModelProvider');
    return {
      open: false,
      tabList: [],
      activeKey: undefined,
      setOpen: () => {
        console.log('RightTabsModel 不支持 setOpen');
      },
      toggleSidebar: () => {
        console.log('RightTabsModel 不支持 toggleSidebar');
      },
      setActiveKey: () => {
        console.log('RightTabsModel 不支持 setActiveKey');
      },
      previewTab: () => {
        console.log('RightTabsModel 不支持 previewTab');
      },
      setTablist: () => {
        console.log('RightTabsModel 不支持 setTablist');
      },
      cleanTabList: () => {
        console.log('RightTabsModel 不支持 cleanTabList');
      },
      addTab: () => {
        console.log('RightTabsModel 不支持 addTab');
      },
      delTab: () => {
        console.log('RightTabsModel 不支持 delTab');
      },
    };
  }
  return context;
}

const ActiveContent: React.FC<TabInfo> = (props) => {
  if (props.type === 'file') {
    return <FilePreview filePreviewUrl={props.filePreviewUrl} fileName={props.fileName} />;
  }
  return <SearchList list={props.list} />;
};

function RightTabsModel() {
  const { setOpen, open, tabList, delTab, activeKey, setActiveKey } = useRightTabsModel();

  const onChange = (key: string) => {
    setActiveKey(key);
  };

  const onEdit = (
    targetKey: React.MouseEvent | React.KeyboardEvent | string,
    action: 'add' | 'remove',
  ) => {
    if (action === 'remove') {
      const targetIndex = tabList.findIndex((item) => item.key === targetKey);
      if (targetIndex > -1) {
        delTab(tabList[targetIndex]);
      }
    }
  };

  const items = tabList.map((item) => ({
    key: item.key,
    label:
      item.type === 'file' ? (
        <div className='flex items-center gap-2'>
          <div className='h-7 w-7'>{getFileSvg(getFileExtension(item.filePreviewUrl), 28)}</div>
          <div className='truncate'>{item.title}</div>
        </div>
      ) : (
        item.title
      ),
    children: <ActiveContent {...item} />,
    closable: true,
  }));

  return (
    <>
      <aside
        className={classNames(
          ' shadow-s1 fixed bottom-0 right-0 z-[111] rounded-t-[16px] transition-[height]  duration-300 md:right-4 md:top-[var(--header-height)]  md:rounded-[16px] md:transition-[width] lg:static lg:shadow-none',
          open && tabList.length
            ? 'h-[85vh] w-full overflow-hidden  md:h-auto md:w-[var(--right-model-width)] lg:pr-[16px]'
            : 'h-[0] w-full md:h-auto md:w-[0]',
          isOverseas
            ? 'md:bottom-[calc(var(--chat-input-height-en)_+_16px)] lg:h-full lg:pb-[17px]'
            : 'md:bottom-[calc(var(--chat-input-height-zh)_+_16px)] lg:pb-[41px]',
        )}
      >
        <div
          className={classNames(
            'bg-col_bg12 md:bg-col_bg15 relative flex h-full flex-col overflow-hidden rounded-t-[16px] md:rounded-[16px]',
            open && tabList.length ? ' border-col_line00 border-[1px] border-solid' : '',
          )}
        >
          <span
            onClick={() => setOpen(false)}
            className={classNames(
              'absolute right-2 top-0 z-10 flex h-[56px] w-12 cursor-pointer items-center justify-center md:right-0 ',
              items.length > 1 && 'to-col_bg12 bg-gradient-to-r from-transparent to-20%',
            )}
          >
            <div className='md:hover:bg-col_bg12 bg-col_bg15 flex h-6 w-6 items-center justify-center rounded-full md:h-8 md:w-8 md:rounded-[8px] md:bg-transparent'>
              <CloseFile />
            </div>
          </span>
          {items.length === 1 ? (
            <div className='flex h-full flex-col overflow-hidden 　pt-4'>
              <div className='md:bg-col_bg15 text-col_text00 truncate pb-[10px] pl-4 pr-10 pt-[18px] text-[16px] font-[500] md:pb-[12px] md:pl-5 md:pt-4'>
                {items[0].label}
              </div>
              <div className='bg-col_line00 mx-2 hidden h-[1px] md:block'></div>
              <div className='flex flex-1 flex-col  overflow-hidden '>
                <ActiveContent {...tabList[0]} />
              </div>
            </div>
          ) : (
            <Tabs
              hideAdd
              onChange={onChange}
              activeKey={activeKey}
              type='editable-card'
              onEdit={onEdit}
              items={items}
              className='right-model-tabs h-full'
            />
          )}
        </div>
      </aside>
      <div
        onClick={() => setOpen(false)}
        className={classNames(
          'bg-col_mask00 fixed bottom-0 left-0 right-0 top-0 z-[110] h-full w-full transition-[opacity]  duration-300 md:hidden',
          open && tabList.length ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      ></div>
    </>
  );
}

export { useRightTabsModel, RightTabsModel, RightTabsModelProvider };
